"use client";

import {
  Children,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  AnimatePresence,
  motion,
  useInView,
  usePageInView,
  useReducedMotion,
} from "motion/react";
import type { Transition } from "motion/react";

import { cn } from "@/lib/utils";
import styles from "./logos-carousel.module.css";

const DEFAULT_COLUMN_COUNT = 3;
const CYCLE_INTERVAL = 2400;
const STAGGER_DELAY = 125;
const TRANSITION_DURATION = 0.5;
const EASE_OUT_QUAD = [0.25, 0.46, 0.45, 0.94] as const;

type WaveDirection = "ltr" | "rtl";

export type LogosCarouselProps = {
  children: ReactNode;
  columnCount?: number;
  direction?: WaveDirection;
  className?: string;
};

/**
 * ncdai LogosCarousel adapted for scroll-spine (no Tailwind utilities — CSS module).
 * @see https://chanhdai.com/components/logos-carousel
 */
export function LogosCarousel({
  children,
  columnCount = DEFAULT_COLUMN_COUNT,
  direction = "ltr",
  className,
}: LogosCarouselProps) {
  const columns = useMemo(
    () => distributeLogos(Children.toArray(children), columnCount),
    [children, columnCount],
  );

  const reduceMotion = useReducedMotion() ?? false;
  const containerRef = useRef<HTMLDivElement>(null);
  const isPageInView = usePageInView();
  const isInView = useInView(containerRef, { margin: "100px" });
  const [step, setStep] = useState(0);
  const shouldPlay = isPageInView && isInView;

  useEffect(() => {
    if (!shouldPlay) return;
    const beatId = setInterval(() => setStep((prev) => prev + 1), CYCLE_INTERVAL);
    return () => clearInterval(beatId);
  }, [shouldPlay]);

  return (
    <div
      ref={containerRef}
      data-slot="logos-carousel"
      className={cn(styles.root, className)}
      style={{ ["--column-count" as string]: columns.length } as CSSProperties}
    >
      {columns.map((columnLogos, columnIndex) => {
        const waveIndex =
          direction === "rtl" ? columns.length - 1 - columnIndex : columnIndex;
        return (
          <LogoColumn
            key={columnIndex}
            logos={columnLogos}
            columnIndex={columnIndex}
            waveIndex={waveIndex}
            activeIndex={step % columnLogos.length}
            reduceMotion={reduceMotion}
          />
        );
      })}
    </div>
  );
}

type LogoColumnProps = {
  logos: ReactNode[];
  columnIndex: number;
  waveIndex: number;
  activeIndex: number;
  reduceMotion: boolean;
};

const LogoColumn = memo(function LogoColumn({
  logos,
  columnIndex,
  waveIndex,
  activeIndex,
  reduceMotion,
}: LogoColumnProps) {
  const transition: Transition = {
    ease: EASE_OUT_QUAD,
    duration: TRANSITION_DURATION,
    delay: waveIndex * (STAGGER_DELAY / 1000),
  };
  const column = getColumnVariants(reduceMotion);
  const logo = getLogoVariants(reduceMotion, transition);

  return (
    <motion.div
      data-slot="logos-carousel-column"
      className={styles.column}
      initial={column.initial}
      animate={column.animate}
      transition={transition}
    >
      <AnimatePresence mode="popLayout">
        <motion.div
          key={`${columnIndex}-${activeIndex}`}
          data-slot="logos-carousel-logo"
          className={styles.logoSlot}
          initial={logo.initial}
          animate={logo.animate}
          exit={logo.exit}
        >
          {logos[activeIndex]}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
});

function getColumnVariants(reduceMotion: boolean) {
  if (reduceMotion) {
    return { initial: { opacity: 0 }, animate: { opacity: 1 } };
  }
  return {
    initial: { opacity: 0, transform: "translateY(60%)" },
    animate: { opacity: 1, transform: "translateY(0%)" },
  };
}

function getLogoVariants(reduceMotion: boolean, transition: Transition) {
  if (reduceMotion) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1, transition },
      exit: { opacity: 0, transition },
    };
  }
  return {
    initial: { opacity: 0, transform: "translateY(60%)", filter: "blur(2px)" },
    animate: {
      opacity: 1,
      transform: "translateY(0%)",
      filter: "blur(0px)",
      transition,
    },
    exit: {
      opacity: 0,
      transform: "translateY(-50%)",
      filter: "blur(3px)",
      transition,
    },
  };
}

function distributeLogos(logos: ReactNode[], columnCount: number): ReactNode[][] {
  const effectiveCount = Math.min(columnCount, logos.length);
  const columns: ReactNode[][] = Array.from({ length: effectiveCount }, () => []);
  logos.forEach((logo, index) => {
    columns[index % effectiveCount].push(logo);
  });
  return columns;
}
